import 'intl-pluralrules';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Localization from 'expo-localization';
import { createInstance } from 'i18next';
import { initReactI18next } from 'react-i18next';

import { STORAGE_KEYS } from '@/config';
import ar from '@/locales/ar.json';
import en from '@/locales/en.json';

export type Locale = 'ar' | 'en';

export function isLocale(value: unknown): value is Locale {
  return value === 'ar' || value === 'en';
}

export function detectDeviceLocale(): Locale {
  const code = Localization.getLocales()[0]?.languageCode;
  return code === 'ar' ? 'ar' : 'en';
}

export async function getStoredLocale(): Promise<Locale | null> {
  try {
    const stored = await AsyncStorage.getItem(STORAGE_KEYS.locale);
    return isLocale(stored) ? stored : null;
  } catch {
    // Storage unavailable (e.g. private browsing) - fall back to device locale.
    return null;
  }
}

export async function persistLocale(locale: Locale): Promise<void> {
  try {
    await AsyncStorage.setItem(STORAGE_KEYS.locale, locale);
  } catch {
    // Non-fatal: the session keeps the chosen locale, it just won't persist.
  }
}

/** App-owned instance (registered as react-i18next's default via initReactI18next). */
const i18n = createInstance();

export async function initI18n(): Promise<Locale> {
  const locale = (await getStoredLocale()) ?? detectDeviceLocale();
  await i18n.use(initReactI18next).init({
    resources: {
      en: { translation: en },
      ar: { translation: ar },
    },
    lng: locale,
    fallbackLng: 'en',
    interpolation: { escapeValue: false },
    returnNull: false,
  });
  return locale;
}

export { i18n };
